import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

import * as api from '@/features/backlog/api';
import { useAppData } from '@/app-data';
import { useIdentity } from '@/features/identity/identity';
import { Member, Role } from '@/features/backlog/types';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { inputCls } from '@/components/ui/form';
import { cn } from '@/components/ui/cn';

type OpenFn = (prefillName?: string, onCreated?: (m: Member) => void) => void;
const Ctx = createContext<OpenFn | null>(null);

/** Opener for the global "New member" modal (or null off-provider). Admin-only in practice. */
export const useNewMember = (): OpenFn | null => useContext(Ctx);

/**
 * Reusable hook returning the SelectPopover create-props ({ onCreate, createLabel }) for member
 * pickers — so an admin can add a brand-new member inline from any assignee/PdM selector, and have
 * them auto-selected on create. Returns {} for non-admins (no create affordance).
 */
export function useMemberCreate() {
  const { me, oidc } = useIdentity();
  const open = useNewMember();
  const canCreate = (!oidc || me?.role === 'admin') && !!open;
  return useCallback(
    (onPick: (id: string) => void): { onCreate?: (q: string) => void; createLabel?: (q: string) => string } => {
      if (!canCreate || !open) return {};
      return {
        onCreate: (q: string) => open(q.trim(), (m) => onPick(m.id)),
        createLabel: (q: string) => (q.trim() ? `Add “${q.trim()}” as a new member` : 'Add a new member'),
      };
    },
    [canCreate, open],
  );
}

/** Provides the New-member modal app-wide. Mount inside AppDataProvider + IdentityProvider. */
export function NewMemberProvider({ children }: { children: ReactNode }) {
  const [req, setReq] = useState<{ name: string; onCreated?: (m: Member) => void } | null>(null);
  const { reloadMembers } = useAppData();
  const open = useCallback<OpenFn>((prefillName = '', onCreated) => setReq({ name: prefillName, onCreated }), []);
  return (
    <Ctx.Provider value={open}>
      {children}
      {req ? (
        <NewMemberModal
          initialName={req.name}
          onClose={() => setReq(null)}
          onCreated={(m) => {
            req.onCreated?.(m);
            setReq(null);
            void reloadMembers();
          }}
        />
      ) : null}
    </Ctx.Provider>
  );
}

function NewMemberModal({ initialName, onClose, onCreated }: { initialName: string; onClose: () => void; onCreated: (m: Member) => void }) {
  // Prefill: if the typed query looks like an email use it as the email, else the name.
  const looksEmail = /@/.test(initialName);
  const [name, setName] = useState(looksEmail ? '' : initialName);
  const [email, setEmail] = useState(looksEmail ? initialName : '');
  const [role, setRole] = useState<Role>('member');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const m = await api.createMember({ name: name.trim(), email: email.trim() || null, role });
      onCreated(m);
    } catch {
      setErr('Could not create this member — the email may already be in use.');
      setBusy(false);
    }
  };

  const roleBtn = (active: boolean) =>
    cn('flex-1 rounded-md px-3 py-1.5 text-[12.5px] font-semibold transition-colors', active ? 'bg-surface-raised text-fg-strong shadow-sm' : 'text-fg-muted hover:text-fg');

  return (
    <Modal open onClose={onClose} title="New member" className="max-w-[420px]">
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-fg-muted">Name</span>
          <input className={inputCls} value={name} autoFocus onChange={(e) => setName(e.target.value)} placeholder="Full name" onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </label>
        <label className="block">
          <span className="mb-1 block text-[12px] font-medium text-fg-muted">Email</span>
          <input className={inputCls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@company.com" onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </label>
        <div>
          <span className="mb-1 block text-[12px] font-medium text-fg-muted">Role</span>
          <div className="inline-flex w-full max-w-[240px] rounded-lg bg-layer p-0.5">
            <button type="button" className={roleBtn(role === 'member')} onClick={() => setRole('member')}>Member</button>
            <button type="button" className={roleBtn(role === 'admin')} onClick={() => setRole('admin')}>Admin</button>
          </div>
        </div>
        {err ? <p className="text-[12px] text-danger">{err}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={submit} disabled={busy || !name.trim()}>
            {busy ? 'Creating…' : 'Create member'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
